import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Star, Award } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import FavoriteButton from "@/components/FavoriteButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getPhotoUrl(photo) {
  if (!photo) return null;
  if (typeof photo === "object") photo = photo.url;
  if (typeof photo !== "string") return null;
  if (photo.startsWith("/api/")) return `${BACKEND_URL}${photo}`;
  return photo;
}

export default function FavoritesPage() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/favorites")
    .then(r => setSpaces(r.data))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="h-6 w-6 text-red-500 fill-red-500" />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="favorites-title">Mes favoris</h1>
      </div>

      {spaces.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-zinc-200 rounded-2xl" data-testid="no-favorites">
          <Heart className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
          <p className="text-zinc-700 font-medium">Aucun terrain en favoris pour le moment</p>
          <p className="text-sm text-zinc-500 mt-1">Cliquez sur le cœur d'un terrain pour le retrouver ici.</p>
          <Link to="/spaces"><Button className="mt-6 bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl">Explorer les terrains</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="favorites-grid">
          {spaces.map((space) => {
            const photo = getPhotoUrl(space.image_url || space.photos?.[0]);
            return (
              <Link to={`/spaces/${space.id}`} key={space.id} className="group">
                <Card className="border border-zinc-200 rounded-2xl overflow-hidden bg-white hover:shadow-lg transition-shadow" data-testid={`fav-card-${space.id}`}>
                  <div className="relative aspect-[4/3] bg-zinc-100 overflow-hidden">
                    {photo ? (
                      <img src={photo} alt={space.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-300">Pas d'image</div>
                    )}
                    <div className="absolute top-3 right-3">
                      <FavoriteButton spaceId={space.id} onToggle={(active) => !active && load()} />
                    </div>
                    {space.is_free && <Badge className="absolute top-3 left-3 bg-amber-500 text-white border-0">Gratuit</Badge>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading font-bold text-zinc-950 truncate">{space.title}</h3>
                      {!space.is_free && <span className="text-emerald-600 font-bold text-sm whitespace-nowrap ml-2">{space.price_per_night?.toFixed(2)} $/nuit</span>}
                    </div>
                    <p className="text-xs text-zinc-500 flex items-center gap-1 mb-2"><MapPin className="h-3 w-3" /> {space.city}{space.region ? `, ${space.region}` : ""}</p>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      {space.avg_rating > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                          {space.avg_rating.toFixed(1)} <span className="text-zinc-400">({space.rating_count})</span>
                        </span>
                      )}
                      {space.owner_is_superstar && (
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] gap-1 px-1.5 py-0 h-4">
                          <Award className="h-2.5 w-2.5" /> Superstar
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
